/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   WorkspaceDetailModal.tsx
 *     Feature         :   117-operational-dashboards
 *     Description     :   Modal showing detailed workspace information and item breakdown
 *     Constitution    :   v1.12.0
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-05
 * =========================================================================================
 */

import React from 'react';
import { Box, Server, Layers, HardDrive } from 'lucide-react';
import { formatEastern, formatEasternDate } from '@/utils/dateFormatter';
import { DetailModal } from './DetailModal';
import type { WorkspaceInventory } from '../../types/operations';
import { unmeasuredSizeNote } from '@/utils/unmeasuredSize';

function formatStorageGb(gb: number | null | undefined): string {
  if (gb == null) return '—';
  if (gb >= 1024) return `${(gb / 1024).toFixed(2)} TB`;
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  return `${(gb * 1024).toFixed(0)} MB`;
}

interface WorkspaceDetailModalProps {
  workspace: WorkspaceInventory | null;
  isLoading?: boolean;
  onClose: () => void;
}

const ITEM_TYPES = [
  { key: 'lakehouse_count' as const, label: 'Lakehouses', color: 'bg-blue-500' },
  { key: 'warehouse_count' as const, label: 'Warehouses', color: 'bg-violet-500' },
  { key: 'notebook_count' as const, label: 'Notebooks', color: 'bg-amber-500' },
  { key: 'pipeline_count' as const, label: 'Pipelines', color: 'bg-emerald-500' },
  { key: 'report_count' as const, label: 'Reports', color: 'bg-red-500' },
  { key: 'semantic_model_count' as const, label: 'Semantic Models', color: 'bg-cyan-500' },
];

export const WorkspaceDetailModal: React.FC<WorkspaceDetailModalProps> = ({ workspace, isLoading = false, onClose }) => {
  if (!workspace && !isLoading) return null;

  return (
    <DetailModal isOpen onClose={onClose} title={workspace?.workspace_name || 'Workspace Details'} size="md">
      {isLoading || !workspace ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
        </div>
      ) : (
      <>
      {/* Subtitle */}
      <p className="mb-6 -mt-1 text-sm text-[var(--color-text-secondary)]">
        {workspace.capacity_name} · {workspace.capacity_type}
      </p>

      {/* Key Metrics */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <MetricCard icon={<Box className="h-5 w-5 text-blue-500" />} label="Total Items" value={workspace.total_item_count} />
        <MetricCard icon={<Server className="h-5 w-5 text-violet-500" />} label="SLA Tier" value={workspace.sla_tier || 'N/A'} />
        {/* Spec 286 T159 item 32: the total keeps sizes the last collection did not measure; say how many. */}
        <MetricCard icon={<HardDrive className="h-5 w-5 text-orange-500" />} label="Managed Data" value={formatStorageGb(workspace.total_storage_gb)} subtext={unmeasuredSizeNote(workspace.sizeNotMeasuredCount)} />
        <MetricCard icon={<Layers className="h-5 w-5 text-emerald-500" />} label="Last Snapshot" value={workspace.last_snapshot_date ? formatEasternDate(workspace.last_snapshot_date) : 'N/A'} />
      </div>

      {/* Item Breakdown */}
      <div className="mb-6">
        <h3 className="mb-3 text-sm font-medium text-[var(--color-text-secondary)]">Item Breakdown</h3>
        <div className="space-y-2">
          {ITEM_TYPES.map(({ key, label, color }) => {
            const count = workspace[key] ?? 0;
            const pct = workspace.total_item_count > 0 ? (count / workspace.total_item_count) * 100 : 0;
            return (
              <div key={key} className="flex items-center gap-3">
                <span className="w-28 text-sm text-[var(--color-text-secondary)]">{label}</span>
                <div className="flex-1">
                  <div className="h-2.5 w-full overflow-hidden rounded-full bg-[var(--color-bg-subtle)]">
                    <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <span className="w-10 text-right text-sm font-medium text-[var(--color-text-primary)]">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail Table */}
      <div>
        <h3 className="mb-3 text-sm font-medium text-[var(--color-text-secondary)]">Details</h3>
        <dl className="divide-y divide-[var(--color-border)] rounded-lg border border-[var(--color-border)]">
          <DetailRow label="Workspace ID" value={workspace.workspace_id} />
          <DetailRow label="Capacity ID" value={workspace.capacity_id} />
          <DetailRow label="Client Account" value={workspace.client_account_id || 'N/A'} />
          <DetailRow label="Capacity Type" value={workspace.capacity_type} />
          <DetailRow label="SLA Tier" value={workspace.sla_tier || 'N/A'} />
          <DetailRow label="Last Snapshot" value={workspace.last_snapshot_date ? formatEastern(workspace.last_snapshot_date) : 'N/A'} />
        </dl>
      </div>
      </>
      )}
    </DetailModal>
  );
};

const MetricCard: React.FC<{ icon: React.ReactNode; label: string; value: string | number; subtext?: string }> = ({ icon, label, value, subtext }) => (
  <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-3">
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-xs text-[var(--color-text-secondary)]">{label}</span>
    </div>
    <p className="mt-1 text-lg font-bold text-[var(--color-text-primary)]">{value}</p>
    {subtext && <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">{subtext}</p>}
  </div>
);

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between px-4 py-2.5">
    <dt className="text-sm text-[var(--color-text-secondary)]">{label}</dt>
    <dd className="text-sm font-medium text-[var(--color-text-primary)]">{value}</dd>
  </div>
);

export default WorkspaceDetailModal;
